import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { assetUrl, handleImgError } from '../utils/imageHelpers';
import WhiteButtonIcon from '../utils/WhiteButtonIcon';
import WhiteButtonTrans from '../utils/WhiteButtonTrans';
import { PAYMENT_METHODS } from '../config/payment';

const ModalPreview = ({ isOpen, onClose, onConfirm, orderData, paymentMethod, onPaymentMethodChange, notes = '', onNotesChange }) => {

  if (!isOpen || !orderData) return null;

  const formatDeliveryDatetime = (dateStr, hour, minute) => {
    if (!dateStr || hour === undefined || minute === undefined) return '';
    // dateStr: 'YYYY-MM-DD'
    const [year, month, day] = dateStr.split('-');
    const ampm = parseInt(hour, 10) >= 12 ? 'PM' : 'AM';
    const displayHour = parseInt(hour, 10) > 12 ? parseInt(hour, 10) - 12 : parseInt(hour, 10) === 0 ? 12 : parseInt(hour, 10);
    return `${day}/${month}/${year} ${displayHour}:${minute.toString().padStart(2, '0')} ${ampm}`;
  };

  const getDeliveryInfo = () => {
    if (orderData.deliveryType === 'pickup') {
      return {
        type: 'Recoger en tienda',
        address: 'La Juanita - Montemorelos, NL'
      };
    } else if (orderData.deliveryType === 'delivery') {
      return {
        type: 'Entrega a domicilio',
        address: 'Tu ubicación actual'
      };
    } else if (orderData.deliveryType === 'someone_else') {
      const fullAddress = `${orderData.customAddress.street.trim()} ${orderData.customAddress.number.trim()}, ${orderData.customAddress.neighborhood.trim()}, Montemorelos, NL`;
      return {
        type: 'Entrega a otra ubicación',
        address: fullAddress
      };
    }
  };

  const deliveryInfo = getDeliveryInfo();

  const handleConfirmOrder = () => {
    // Prepare final order data with promotions and discounted price
    const finalOrderData = {
      customerInfo: orderData.customerInfo,
      deliveryType: orderData.deliveryType,
      location: orderData.location,
      customAddress: orderData.customAddress,
      items: orderData.items,
      promotions: orderData.promotions || {},
      originalTotal: orderData.originalTotal,
      totalPrice: orderData.totalPrice,
      subtotalPreDiscount: orderData.subtotalPreDiscount,
      customerDiscount: orderData.customerDiscount,
      customerDiscountPct: orderData.customerDiscountPct,
      employeeDiscount: orderData.employeeDiscount,
      benefitCombos: orderData.benefitCombos,
      couponId: orderData.couponId,
      couponDiscount: orderData.couponDiscount,
      discountPercentage: orderData.discountPercentage,
      hasPromotions: orderData.hasPromotions,
      isNewCustomer: orderData.isNewCustomer,
      packingConfig: orderData.packingConfig || null,
      comboGroups: orderData.comboGroups || [],
      paymentMethod: paymentMethod || PAYMENT_METHODS.CASH,
      notes: notes.trim()
    };

    onConfirm(finalOrderData);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <motion.div
            className="relative bg-gradient-to-br from-red-900/95 via-red-800/95 to-red-950/95 backdrop-blur-sm rounded-2xl shadow-2xl max-w-sm w-full mx-4 overflow-hidden border border-white/20 max-h-[90vh] overflow-y-auto custom-scrollbar"
            initial={{ scale: 0.8, opacity: 0, y: 50 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.8, opacity: 0, y: 50 }}
            transition={{
              type: "spring",
              stiffness: 300,
              damping: 30,
              duration: 0.4
            }}
          >

            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-white/80 hover:text-white transition-all duration-200 z-10 p-2 hover:bg-white/10 rounded-full"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Background glow effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-red-400/20 to-red-300/10 rounded-2xl blur-xl scale-105 opacity-30"></div>

            {/* Content */}
            <div className="relative p-6">
              {/* Header */}
              <div className="text-center mb-6">
                <div className="w-16 h-16 mx-auto mb-4 bg-white/20 rounded-full flex items-center justify-center">
                  <img
                    src={assetUrl('/assets/check-circle.svg')}
                    alt="Revisar"
                    className="w-8 h-8 brightness-200"
                    onError={handleImgError}
                  />
                </div>

                <h2 className="text-xl font-bold text-white mb-2">
                  Revisar Pedido
                </h2>
                <p className="text-white/90 text-sm">
                  Verifica que todo esté correcto antes de confirmar
                </p>
              </div>

              {/* Order Preview Ticket */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 p-4 mb-6">
                {/* Fecha/hora de entrega del pedido */}
                <div className="mb-4 pb-3 border-b border-white/20">
                  <h4 className="text-white font-semibold text-xs mb-2">Fecha de entrega</h4>
                  <div className="flex justify-between items-center">
                    <span className="text-white/70 text-xs">Fecha y hora:</span>
                    <span className="text-white text-xs font-medium">
                      {formatDeliveryDatetime(
                        orderData.customerInfo.date,
                        orderData.customerInfo.hour,
                        orderData.customerInfo.minute
                      )}
                    </span>
                  </div>
                </div>

                {/* Combos armados (precio fijo) y platos armados (precio lleno) */}
                {orderData.comboGroups && orderData.comboGroups.length > 0 && (
                  <div className="mb-4 pb-3 border-b border-white/20">
                    <h4 className="text-white font-semibold text-xs mb-3 border-b border-white/20 pb-1">
                      {(() => {
                        const groups = orderData.comboGroups;
                        const hasCombo = groups.some(g => !g.meta?.noDiscount);
                        const hasPlato = groups.some(g => g.meta?.noDiscount);
                        return hasCombo && hasPlato ? 'Combos y platos' : hasPlato ? 'Platos' : 'Combos';
                      })()}
                    </h4>
                    <div className="space-y-2">
                      {orderData.comboGroups.map((group, gi) => (
                        <div key={gi} className="bg-white/5 rounded-lg p-2.5">
                          <div className="flex justify-between items-center mb-1">
                            <p className={`text-[10px] uppercase tracking-wider ${group.meta?.noDiscount ? 'text-white/60' : 'text-amber-300'}`}>{group.meta?.name}</p>
                            <span className="text-white font-bold text-xs">${group.meta?.price} MXN</span>
                          </div>
                          {group.items.map((it, ii) => (
                            <div key={ii} className="flex justify-between items-center">
                              <p className="text-white/70 text-xs">{it.title}</p>
                              <span className="text-white/50 text-[10px]">Porción</span>
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Per-charola pricing view (when packing has pricing data) */}
                {orderData.packingConfig?.packingTotal != null ? (
                  <>
                    <div className="mb-4 pb-3 border-b border-white/20">
                      <h4 className="text-white font-semibold text-xs mb-3 border-b border-white/20 pb-1">
                        Desglose del Pedido
                      </h4>
                      <div className="space-y-3">
                        {orderData.packingConfig.charolas.map((charola, idx) => {
                          const linkedItems = (orderData.packingConfig.sueltos || []).filter(s => s.linkedToCharola === idx + 1);
                          return (
                          <div key={idx} className="bg-white/5 rounded-lg p-2.5">
                            <div className="flex justify-between items-center mb-1.5">
                              <p className="text-white/60 text-[10px] uppercase tracking-wider">Charola {idx + 1}</p>
                              <span className="text-white font-bold text-xs">${charola.finalPrice} MXN</span>
                            </div>
                            {/* Main dish */}
                            <div className="flex justify-between items-center border-l-4 border-red-500 pl-2">
                              <p className="text-white/90 text-xs">{charola.main_name}</p>
                              <span className="text-white/50 text-[10px]">${charola.main_price}</span>
                            </div>
                            {/* Sides */}
                            {charola.sides_names && charola.sides_names.map((sideName, sIdx) => (
                              <div key={sIdx} className="flex justify-between items-center border-l-4 border-amber-500 pl-2">
                                <p className="text-white/70 text-xs">{sideName}</p>
                                <span className="text-white/50 text-[10px]">${charola.sides_prices[sIdx]}</span>
                              </div>
                            ))}
                            {/* Linked combo items (beverages/desserts) */}
                            {linkedItems.map((item, lIdx) => (
                              <div key={`linked-${lIdx}`} className="flex justify-between items-center border-l-4 border-blue-500 pl-2">
                                <p className="text-white/70 text-xs">{item.name}</p>
                                <span className="text-white/50 text-[10px]">${item.price}</span>
                              </div>
                            ))}
                            {/* Combo badge or individual sum note */}
                            {charola.comboPrice !== null ? (
                              <p className="text-green-300/90 text-[10px] mt-1.5 flex items-center gap-1">
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                {charola.promoName || 'Combo'} — ${charola.comboPrice}
                                {charola.individualSum > charola.comboPrice && (
                                  <span className="text-white/40 line-through ml-1">${charola.individualSum}</span>
                                )}
                              </p>
                            ) : (
                              <p className="text-white/40 text-[10px] mt-1.5">
                                Sin promoción — ${charola.individualSum}
                              </p>
                            )}
                          </div>
                          );
                        })}

                        {/* Sueltos (only items NOT linked to a charola) */}
                        {(() => {
                          const trueSueltos = (orderData.packingConfig.sueltos || []).filter(s => !s.linkedToCharola);
                          return trueSueltos.length > 0 && (
                          <div className="bg-white/5 rounded-lg p-2.5">
                            <div className="flex justify-between items-center mb-1.5">
                              <p className="text-white/60 text-[10px] uppercase tracking-wider">Sueltos</p>
                              <span className="text-white font-bold text-xs">${orderData.packingConfig.sueltosTotal} MXN</span>
                            </div>
                            {trueSueltos.map((item, idx) => (
                              <div key={idx} className="flex justify-between items-center">
                                <p className="text-white/70 text-xs">{item.name}</p>
                                <span className="text-white/50 text-[10px]">${item.price}</span>
                              </div>
                            ))}
                          </div>
                          );
                        })()}
                      </div>
                    </div>

                    {/* Delivery Info */}
                    <div className="mb-4 pb-3 border-b border-white/20">
                      <div className="flex justify-between items-start">
                        <span className="text-white/70 text-xs">Entrega:</span>
                        <div className="text-right flex-1 ml-2">
                          <p className="text-white text-xs font-medium">{deliveryInfo.type}</p>
                          <p className="text-white/60 text-xs mt-0.5 break-words">{deliveryInfo.address}</p>
                        </div>
                      </div>
                    </div>

                    {/* Total */}
                    <div className="border-t border-white/20 pt-3">
                      {orderData.totalPrice < orderData.originalTotal ? (
                        <>
                          <div className="flex justify-between items-center">
                            <span className="text-white/60 text-xs">Total sin descuento</span>
                            <span className="text-white/40 text-xs line-through">
                              ${orderData.originalTotal} MXN
                            </span>
                          </div>
                          <div className="flex justify-between items-center mt-1.5 bg-green-500/15 rounded-lg px-2.5 py-1.5">
                            <span className="text-green-300 text-xs font-semibold flex items-center gap-1">
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                              ¡Te ahorraste!
                            </span>
                            <span className="text-green-300 text-sm font-bold">
                              -${orderData.originalTotal - orderData.totalPrice} MXN
                            </span>
                          </div>
                          {orderData.customerDiscount > 0 && (
                            <div className="flex justify-between items-center mt-1 px-2.5">
                              <span className="text-green-300 text-[11px] font-medium">
                                {orderData.customerDiscountPct === 100 ? 'Cortesía' : `Descuento cliente (${orderData.customerDiscountPct}%)`}
                              </span>
                              <span className="text-green-300 text-[11px] font-bold">-${orderData.customerDiscount} MXN</span>
                            </div>
                          )}
                          {orderData.employeeDiscount > 0 && (
                            <div className="flex justify-between items-center mt-1 px-2.5">
                              <span className="text-blue-300 text-[11px] font-medium">Incluye beneficio de empleado</span>
                              <span className="text-blue-300 text-[11px] font-bold">-${orderData.employeeDiscount} MXN</span>
                            </div>
                          )}
                          {orderData.couponDiscount > 0 && (
                            <div className="flex justify-between items-center mt-1 px-2.5">
                              <span className="text-purple-300 text-[11px] font-medium">Cupón aplicado</span>
                              <span className="text-purple-300 text-[11px] font-bold">-${orderData.couponDiscount} MXN</span>
                            </div>
                          )}
                          <div className="flex justify-between items-center mt-2 pt-2 border-t border-white/20">
                            <span className="text-white font-bold text-sm">NETO A PAGAR:</span>
                            <span className="text-white font-bold text-lg">
                              ${orderData.totalPrice} MXN
                            </span>
                          </div>
                        </>
                      ) : (
                        <div className="flex justify-between items-center">
                          <span className="text-white font-bold text-sm">TOTAL:</span>
                          <span className="text-white font-bold text-lg">
                            ${orderData.totalPrice} MXN
                          </span>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    {/* Fallback: original items list (no packing pricing) */}
                    <div className="mb-4 pb-3 border-b border-white/20">
                      <h4 className="text-white font-semibold text-xs mb-3 border-b border-white/20 pb-1">
                        Artículos del Pedido
                      </h4>
                      <div className="space-y-2">
                        {(orderData.items || []).filter(i => !i.comboGroupId).map((item, index) => (
                          <div key={item.id || index} className="flex justify-between items-start text-xs">
                            <div className="flex-1">
                              <p className="text-white/90 leading-tight">
                                {item.quantity}x {item.title}
                              </p>
                              {item.presentationLabel && (
                                <p className="text-white/50 text-[10px]">
                                  {item.presentationLabel}{item.qty && item.unit ? ` · ${item.qty} ${item.unit}` : ''}
                                </p>
                              )}
                              <p className="text-white/60 text-[10px]">
                                ${item.price} c/u
                              </p>
                            </div>
                            <span className="text-white font-medium ml-2">
                              ${item.price * item.quantity}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Delivery Info */}
                    <div className="mb-4 pb-3 border-b border-white/20">
                      <div className="flex justify-between items-start">
                        <span className="text-white/70 text-xs">Entrega:</span>
                        <div className="text-right flex-1 ml-2">
                          <p className="text-white text-xs font-medium">{deliveryInfo.type}</p>
                          <p className="text-white/60 text-xs mt-0.5 break-words">{deliveryInfo.address}</p>
                        </div>
                      </div>
                    </div>

                    {/* Order Summary */}
                    <div className="space-y-2 mb-4">
                      <h4 className="text-white font-semibold text-xs border-b border-white/20 pb-1">
                        Resumen del Pedido
                      </h4>
                      <div className="flex justify-between items-center">
                        <span className="text-white/80 text-xs">
                          {(orderData.items || []).filter(i => !i.comboGroupId).length} artículo{(orderData.items || []).filter(i => !i.comboGroupId).length !== 1 ? 's' : ''}
                        </span>
                        <span className="text-white font-semibold text-sm">
                          ${orderData.originalTotal || orderData.totalPrice} MXN
                        </span>
                      </div>
                      {orderData.hasPromotions && orderData.discountPercentage > 0 && (
                        <div className="flex justify-between items-center text-green-300">
                          <span className="text-xs">
                            Descuento aplicado
                          </span>
                          <span className="text-sm font-semibold">
                            -${(orderData.originalTotal - orderData.totalPrice).toFixed(0)} MXN
                          </span>
                        </div>
                      )}
                      {orderData.customerDiscount > 0 && (
                        <div className="flex justify-between items-center text-green-300">
                          <span className="text-xs">
                            {orderData.customerDiscountPct === 100 ? 'Cortesía' : `Descuento cliente (${orderData.customerDiscountPct}%)`}
                          </span>
                          <span className="text-sm font-semibold">-${orderData.customerDiscount} MXN</span>
                        </div>
                      )}
                      {orderData.employeeDiscount > 0 && (
                        <div className="flex justify-between items-center text-blue-300">
                          <span className="text-xs">Incluye beneficio de empleado</span>
                          <span className="text-sm font-semibold">-${orderData.employeeDiscount} MXN</span>
                        </div>
                      )}
                      {orderData.couponDiscount > 0 && (
                        <div className="flex justify-between items-center text-purple-300">
                          <span className="text-xs">Cupón aplicado</span>
                          <span className="text-sm font-semibold">-${orderData.couponDiscount} MXN</span>
                        </div>
                      )}
                    </div>

                    {/* Total */}
                    <div className="border-t border-white/20 pt-3">
                      <div className="flex justify-between items-center">
                        <span className="text-white font-bold text-sm">TOTAL:</span>
                        <span className="text-white font-bold text-lg">
                          ${orderData.totalPrice} MXN
                        </span>
                      </div>
                      {orderData.hasPromotions && (
                        <p className="text-green-300/90 text-xs font-medium">
                          ¡Promoción aplicada!
                        </p>
                      )}
                    </div>
                  </>
                )}
              </div>

              {/* Método de pago */}
              <div className="mb-6">
                {orderData.totalPrice === 0 ? (
                  <div className="rounded-xl bg-green-500/15 px-3 py-2.5 text-center">
                    <span className="text-green-300 text-xs font-semibold">Cortesía — nada por pagar</span>
                  </div>
                ) : (
                  <>
                    <h4 className="text-white font-semibold text-xs mb-2">¿Cómo vas a pagar?</h4>
                    <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="Método de pago">
                      <button
                        type="button"
                        role="radio"
                        aria-checked={paymentMethod !== PAYMENT_METHODS.TRANSFER}
                        onClick={() => onPaymentMethodChange?.(PAYMENT_METHODS.CASH)}
                        className={`flex flex-col items-center justify-center gap-1 min-h-[60px] px-2 py-2.5 rounded-xl border cursor-pointer transition-colors duration-200 ${
                          paymentMethod !== PAYMENT_METHODS.TRANSFER
                            ? 'bg-white text-red-900 border-white shadow-lg'
                            : 'bg-white/10 text-white border-white/20 hover:bg-white/15'
                        }`}
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span className="text-xs font-bold leading-none">Efectivo</span>
                        <span className={`text-[10px] leading-none ${paymentMethod !== PAYMENT_METHODS.TRANSFER ? 'text-red-900/70' : 'text-white/60'}`}>
                          Al recibir tu pedido
                        </span>
                      </button>

                      <button
                        type="button"
                        role="radio"
                        aria-checked={paymentMethod === PAYMENT_METHODS.TRANSFER}
                        onClick={() => onPaymentMethodChange?.(PAYMENT_METHODS.TRANSFER)}
                        className={`flex flex-col items-center justify-center gap-1 min-h-[60px] px-2 py-2.5 rounded-xl border cursor-pointer transition-colors duration-200 ${
                          paymentMethod === PAYMENT_METHODS.TRANSFER
                            ? 'bg-white text-red-900 border-white shadow-lg'
                            : 'bg-white/10 text-white border-white/20 hover:bg-white/15'
                        }`}
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 21v-8.25M15.75 21v-8.25M8.25 21v-8.25M3 9l9-6 9 6m-1.5 12V10.332A48.36 48.36 0 0012 9.75c-2.551 0-5.056.2-7.5.582V21M3 21h18M12 6.75h.008v.008H12V6.75z" />
                        </svg>
                        <span className="text-xs font-bold leading-none">Transferencia</span>
                        <span className={`text-[10px] leading-none ${paymentMethod === PAYMENT_METHODS.TRANSFER ? 'text-red-900/70' : 'text-white/60'}`}>
                          Datos al confirmar
                        </span>
                      </button>
                    </div>
                    {paymentMethod === PAYMENT_METHODS.TRANSFER && (
                      <p className="text-white/70 text-[11px] mt-2 leading-snug">
                        Al confirmar te mostramos la cuenta bancaria y tu referencia de pago.
                      </p>
                    )}
                  </>
                )}
              </div>

              {/* Notas para cocina: lo que el cliente quiere avisar (sin cebolla,
                  salsa extra). Antes solo se podía pedir por teléfono. */}
              {onNotesChange && (
                <div className="mb-6">
                  <label htmlFor="notas-pedido" className="block text-white font-semibold text-xs mb-2">
                    ¿Algo que debamos saber? <span className="font-normal text-white/60">(opcional)</span>
                  </label>
                  <textarea
                    id="notas-pedido"
                    value={notes}
                    onChange={(e) => onNotesChange(e.target.value)}
                    rows={2}
                    maxLength={300}
                    placeholder="Ej. sin cebolla, salsa extra, limones, para 3 personas"
                    className="w-full p-3 text-sm rounded-xl bg-white/10 text-white placeholder-white/50 border border-white/20 focus:border-white/40 focus:outline-none focus:ring-2 focus:ring-white/20 transition-all duration-200 resize-none"
                  />
                </div>
              )}

              {/* Action Buttons */}
              <div className="grid grid-cols-2 gap-3">
                <WhiteButtonIcon
                  text="Levantar Pedido"
                  onClick={handleConfirmOrder}
                  className="justify-center font-bold text-sm rounded-xl transition-all duration-200 h-10 hover:scale-[1.02] shadow-lg"
                  iconPath="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />

                <WhiteButtonTrans
                  text="Regresar"
                  onClick={onClose}
                  className="justify-center font-semibold text-sm rounded-xl transition-all duration-200 hover:bg-white/10 h-10"
                />
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ModalPreview;
